/**
 * The backoffice's store seam: live lists under workspaces/revia and the
 * `approveAccessRequest`, `setAccessRequestStatus`, `signLicence`,
 * `connectClient` and `gitHubConnection` callables.
 */
import { snapshotQuery$ } from "@mhmo91/rx";
import {
	AccessRequest,
	ApproveAccessRequest,
	ConnectClient,
	GitHubConnection,
	LandingViews,
	Licence,
	REVIA_ORG_ID,
	SetAccessRequestStatus,
	SignLicence,
} from "@revia/types";
import { collection, documentId, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { map } from "rxjs";
import { db, functions } from "../firebase.ts";

export function accessRequests$() {
	return snapshotQuery$(
		query(collection(db, "workspaces", REVIA_ORG_ID, "accessRequests")),
	).pipe(
		// One document that will not parse is logged and left out, never
		// allowed to blank every other request on the list.
		map((snap) =>
			snap.docs.flatMap((d) => {
				const request = AccessRequest.AccessRequestSchema.safeParse(d.data());
				if (request.success) return [{ id: d.id, ...request.data }];
				console.error("[backoffice] access request skipped", d.id, request.error);
				return [];
			}),
		),
	);
}

export function licences$() {
	return snapshotQuery$(
		query(collection(db, "workspaces", REVIA_ORG_ID, "licences")),
	).pipe(
		// As above: one unreadable licence is logged and left out.
		map((snap) =>
			snap.docs.flatMap((d) => {
				const licence = Licence.LicenceSchema.safeParse(d.data());
				if (licence.success) return [{ id: d.id, ...licence.data }];
				console.error("[backoffice] licence skipped", d.id, licence.error);
				return [];
			}),
		),
	);
}

/** The landing's daily counts for the UTC days `from` to `to` (`yyyy-mm-dd`),
 *  each keyed by its day. A day nobody arrived on has no document. */
export function landingViews$(from: string, to: string) {
	return snapshotQuery$(
		query(
			collection(db, "workspaces", REVIA_ORG_ID, "landingViews"),
			where(documentId(), ">=", from),
			where(documentId(), "<=", to),
		),
	).pipe(
		map((snap) =>
			snap.docs.map((d) => ({
				day: d.id,
				...LandingViews.LandingViewsSchema.parse(d.data()),
			})),
		),
	);
}

export async function approveAccessRequest(
	input: ApproveAccessRequest.ApproveAccessRequestInput,
): Promise<ApproveAccessRequest.ApproveAccessRequestOutput> {
	return ApproveAccessRequest.ApproveAccessRequestOutput.parse(
		(
			await httpsCallable<
				ApproveAccessRequest.ApproveAccessRequestInput,
				ApproveAccessRequest.ApproveAccessRequestOutput
			>(
				functions,
				"approveAccessRequest",
			)(ApproveAccessRequest.ApproveAccessRequestInput.parse(input))
		).data,
	);
}

export async function setAccessRequestStatus(
	requestId: string,
	status: SetAccessRequestStatus.SetAccessRequestStatusInput["status"],
): Promise<SetAccessRequestStatus.SetAccessRequestStatusOutput> {
	return SetAccessRequestStatus.SetAccessRequestStatusOutput.parse(
		(
			await httpsCallable<
				SetAccessRequestStatus.SetAccessRequestStatusInput,
				SetAccessRequestStatus.SetAccessRequestStatusOutput
			>(
				functions,
				"setAccessRequestStatus",
			)(SetAccessRequestStatus.SetAccessRequestStatusInput.parse({ requestId, status }))
		).data,
	);
}

export async function signLicence(
	input: SignLicence.SignLicenceInput,
): Promise<SignLicence.SignLicenceOutput> {
	return SignLicence.SignLicenceOutput.parse(
		(
			await httpsCallable<SignLicence.SignLicenceInput, SignLicence.SignLicenceOutput>(
				functions,
				"signLicence",
			)(SignLicence.SignLicenceInput.parse(input))
		).data,
	);
}

export async function connectClient(
	input: ConnectClient.ConnectClientInput,
): Promise<ConnectClient.ConnectClientOutput> {
	return ConnectClient.ConnectClientOutput.parse(
		(
			await httpsCallable<ConnectClient.ConnectClientInput, ConnectClient.ConnectClientOutput>(
				functions,
				"connectClient",
			)(ConnectClient.ConnectClientInput.parse(input))
		).data,
	);
}

export async function gitHubConnection(): Promise<GitHubConnection.GitHubConnectionOutput> {
	return GitHubConnection.GitHubConnectionOutput.parse(
		(
			await httpsCallable<GitHubConnection.GitHubConnectionInput, GitHubConnection.GitHubConnectionOutput>(
				functions,
				"gitHubConnection",
			)({})
		).data,
	);
}
